import { useSelector } from "react-redux";
import { useSnackbar } from "../product/hooks/SnackbarProvider";
import useGetDevice from "../product/hooks/useGetDevice";
import useViewportTablePageSize, { initialTablePageSize } from "../product/hooks/useViewportTablePageSize";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { getOrderDetails, returnsList } from "../../../config/Services";
import { convertToLocalDateTime } from "../product/all-products/Middleware/commonUtils";
import { Box, CircularProgress, Pagination, Typography } from "@mui/material";
import OrderCard from "../product/all-products/components/OrderCard/OrderCard";
import SoftBox from "../../../components/SoftBox";
import Spinner from "../../../components/Spinner";
// import StyledSelect from "../custom/StyledSelect";          // commented — old toolbar
// import StyledDatePicker from "../custom/StyledDatePicker";  // commented — old toolbar
// import StyledButton from "../custom/StyledButton";          // commented — old toolbar
// import { DataGrid } from "@mui/x-data-grid";               // replaced with CustomerPaginatedTable
import { OrderIdValidationModal } from "./components/orderIdValidationModal";
import { getReturnedProducts } from "../product/all-products/redux/Reducers/paginationReducer";
import { getFromLocalStorage } from "../../../utils/localStorageHelpers";
// import DateRangeIcon from "@mui/icons-material/DateRange";  // commented — old toolbar
import "./Returns.css";
import { OrderDetailsPage } from "../product/orders";
import useAuthModal from "../product/hooks/useAuthModal";
import { customEventTrigger } from "../../../analytics";
// import { PosCustomButton } from "../../../../../../customComponents/PosCustomButton/PosCustomButton"; // commented — replaced
// import CustomerPaginatedTable from "../CustomerPaginatedTable/CustomerPaginatedTable";
import clsx from "clsx";
import CustomerPaginatedTable from "../product/all-products/CustomerV2Preview/Component/CustomComponent/CustomerPaginatedTable";

// ── Inline SVG — Undo2 (lucide) ──────────────────────────────────────────────
const Undo2Icon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="returns-header__new-btn-icon"
  >
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11" />
  </svg>
);

const cashIcon =
  "https://static.vecteezy.com/system/resources/previews/005/567/661/original/rupee-icon-indian-currency-symbol-illustration-coin-symbol-free-vector.jpg";
const cardIcon = "https://pngimg.com/uploads/mastercard/mastercard_PNG24.png";
const upiIcon = "https://logodix.com/logo/1763598.png";
const splitIcon = "https://cdn.onlinewebfonts.com/svg/img_3109.png";

// ── Order-type filter tabs ────────────────────────────────────────────────────
const ORDER_TYPE_TABS = [
  { label: "All", value: "all", testId: "button-returns-order-type-all" },
  { label: "Delivery", value: "DELIVERY", testId: "button-returns-order-type-delivery" },
  { label: "Takeaway", value: "TAKE_AWAY", testId: "button-returns-order-type-takeaway" },
  { label: "Dine In", value: "DINE_IN", testId: "button-returns-order-type-dine-in" },
];

// ── CustomerPaginatedTable column definitions ─────────────────────────────────
const RETURNS_COLUMNS = [
  {
    key: "date",
    label: "Date",
    width: "11%",
    render: (row) => (
      <div className="returns-cell__date">
        <span className="returns-cell__date-primary">{row?.date_display}</span>
        <span className="returns-cell__date-secondary">{row?.time_display}</span>
      </div>
    ),
  },
  {
    key: "order_id",
    label: "Order ID",
    width: "11%",
    render: (row) => (
      <span className="returns-cell__id returns-cell__id--order">{row?.order_id ?? "-----"}</span>
    ),
  },
  {
    key: "return_id",
    label: "Return ID",
    width: "11%",
    render: (row) => (
      <span className="returns-cell__id returns-cell__id--return">{row?.return_id ?? "-----"}</span>
    ),
  },
  {
    key: "customer_number_name",
    label: "Customer",
    width: "17%",
    render: (row) => (
      <address className="returns-cell__customer" style={{ fontStyle: "normal" }}>
        <span className="returns-cell__customer-name">{row?.customer_name ?? "-----"}</span>
        <span className="returns-cell__customer-phone">{row?.customer_phone ?? ""}</span>
      </address>
    ),
  },
  {
    key: "cashier_name",
    label: "Cashier Name",
    width: "12%",
    render: (row) => <span className="returns-cell__cashier">{row?.cashier_name ?? "-----"}</span>,
  },
  {
    key: "returned_count",
    label: "Returned / Qty",
    width: "14%",
    render: (row) => {
      return <data value={row?.returned_count}>{row?.returned_count ?? "N/A"}</data>;
    },
  },
  {
    key: "return_amount",
    label: "Amount",
    width: "9%",
    render: (row) => (
      <strong className="returns-cell__amount">
        {row?.return_amount != null
          ? `₹${Number(row?.return_amount).toLocaleString("en-IN")}`
          : "N/A"}
      </strong>
    ),
  },
  {
    key: "payment_mode",
    label: "Mode",
    width: "7%",
    render: (row) => {
      return <span className="returns-cell__mode-pill">{row?.payment_mode ?? "—"}</span>;
    },
  },
  {
    key: "status",
    label: "Status",
    width: "8%",
    render: (row) => {
      const val = row?.status ?? "";
      const isRefunded = val?.toUpperCase() === "REFUNDED";
      return (
        <mark
          className={clsx(
            "returns-cell__status-badge",
            isRefunded && "returns-cell__status-badge--refunded"
          )}
          style={{ background: undefined }} // let CSS handle it
        >
          {val || "-----"}
        </mark>
      );
    },
  },
];

const Returns = ({ orderValue: propOrderValue, date: propDate }) => {
  const isMobile = useGetDevice();
  const showSnackbar = useSnackbar();
  const getReturnsText = useSelector((state) => state.pagination.return_text);
  const sessionId = localStorage.getItem("sessionId");

  const orderValue = propOrderValue || { value: "Session orders", label: "Session Returns" };
  const dispatch = useDispatch();
  const date = propDate || null;
  const [dataRows, setTableRows] = useState([]);
  const [noData, setNoData] = useState(false);
  const [state, setState] = useState(false);
  const [update, setUpdate] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowDetails, setRowDetails] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [open, setOpen] = useState(false);
  const [orderIdVal, setOrderIdVal] = useState("");
  const [orderItems, setOrderItems] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const returnItemArray = useSelector(getReturnedProducts);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { renderAuthModal, openAuthModal } = useAuthModal();
  const [pageState, setPageState] = useState({
    loader: false,
    datRows: [],
    total: 0,
    page: 1,
    pageSize: isMobile ? 8 : initialTablePageSize(),
  });
  const tableRef = useViewportTablePageSize(setPageState, !isMobile);
  const [returnId, setReturnId] = useState("");
  const retailType = localStorage.getItem("retailType");

  // ── New: active order-type tab ────────────────────────────────────────────
  const [activeOrderType, setActiveOrderType] = useState("all");

  // ── Old MUI DataGrid column defs (commented out, preserved) ──────────────
  /*
  const columns = [
    {
      field: "date",
      headerName: "Date",
      minWidth: 180,
      flex: 1,
      headerClassName: "datagrid-columns",
      headerAlign: "center",
      cellClassName: "datagrid-rows",
      align: "center",
    },
    {
      field: "order_id",
      headerName: "Order Id",
      minWidth: 120,
      flex: 1,
      headerClassName: "datagrid-columns",
      headerAlign: "center",
      cellClassName: "datagrid-rows",
      align: "center",
    },
    {
      field: "return_id",
      headerName: "Return Id",
      minWidth: 120,
      flex: 1,
      headerClassName: "datagrid-columns",
      headerAlign: "center",
      cellClassName: "datagrid-rows",
      align: "center",
    },
    {
      field: "customer_number_name",
      headerName: "Customer Number/Name",
      minWidth: 150,
      flex: 1,
      headerClassName: "datagrid-columns",
      headerAlign: "center",
      cellClassName: "datagrid-rows",
      align: "center",
    },
    {
      field: "cashier_name",
      headerName: "Cashier Name",
      minWidth: 150,
      flex: 1,
      headerClassName: "datagrid-columns",
      headerAlign: "center",
      cellClassName: "datagrid-rows",
      align: "center",
    },
    {
      field: "returned_count",
      headerName: "Returned Items/Qty",
      minWidth: 50,
      flex: 1,
      headerClassName: "datagrid-columns",
      headerAlign: "center",
      cellClassName: "datagrid-rows",
      align: "center",
    },
    {
      field: "return_amount",
      headerName: "Return Amount",
      minWidth: 50,
      flex: 1,
      headerClassName: "datagrid-columns",
      headerAlign: "center",
      cellClassName: "datagrid-rows",
      align: "center",
    },
    {
      field: "payment_mode",
      headerName: "Payment Mode",
      minWidth: 120,
      flex: 1,
      headerClassName: "datagrid-columns",
      headerAlign: "center",
      cellClassName: "datagrid-rows",
      align: "center",
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{params.value}</span>
           <img
            src={renderPaymentIcon(params.row.payment_mode)}
            alt="payment-icon"
            width="20px"
            height="20px"
            style={{ marginLeft: 8 }}
          />
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      flex: 1,
      headerClassName: "datagrid-columns",
      headerAlign: "center",
      cellClassName: "datagrid-rows",
      align: "center",
      cellClassName: (params) => {
        if (params.value == null) {
          return "";
        }
        return clsx("super-app", {
          Paid: params.value === "PAID",
          Create: params.value === "CREATED",
          Adjust: params.value === "ADJUSTED",
          Pending: params.value === "PAYMENT_PENDING",
          Cancel: params.value === "CANCELLED",
          Payment_Cancel: params.value === "PAYMENT_CANCELLED",
          Partial_Paid: params.value === "PARTIALLY_PAID",
        });
      },
      renderCell: (cellValues) => {
        return (
          <div
            className="table-dark-wrapper"
            style={{
              color: cellValues.value === "PENDING" ? "#EBA85A" : "#0B742C",
            }}
          >
            {cellValues.value}
          </div>
        );
      },
    },
  ];
  */

  const handleSelectChange = (e) => {
    customEventTrigger("pos_return_page_dropdown", { value: e?.value });
    // setOrderValue(e); // setOrderValue not defined — controlled by parent via prop
    setPageState((old) => ({ ...old, page: 1 }));
  };

  let locId = localStorage.getItem("locId");
  const { uidx } = getFromLocalStorage("retailUserDetails");

  const renderPaymentIcon = (paymentMethod) => {
    switch (paymentMethod) {
      case "CASH":
        return cashIcon;
      case "CARD":
        return cardIcon;
      case "UPI":
        return upiIcon;
      case "SODEXO":
        return sodexoIcon;
      case "SPLIT":
        return splitIcon;
      default:
        return "";
    }
  };

  // ─── FIX: Reset page to 1 whenever filter or date changes ───────────────────
  useEffect(() => {
    setPageState((old) => ({ ...old, page: 1 }));
  }, [orderValue?.value, date, activeOrderType]);

  // ─── FIX: Main fetch effect ───────────────────────────────────────────────
  useEffect(() => {
    if (orderValue?.value === "In-store Delivery") {
      showSnackbar("Not available, coming soon ", "info");
      return;
    }

    let filterObject;

    if (getReturnsText?.length === 0) {
      filterObject =
        orderValue?.value === "Session orders"
          ? {
              page: pageState?.page - 1,
              pageSize: pageState.pageSize,
              locationId: locId,
              orderType: retailType === "RESTAURANT" ? "RESTAURANT_ORDER" : "POS_ORDER",
              orderTypes: ["SWIGGY_ORDER", "ZOMATO_ORDER", "RESTAURANT_ORDER", "ONDC_ORDER"],
              sessionId,
              ...(activeOrderType !== "all" && { subOrderTypes: [activeOrderType] }),
            }
          : orderValue?.value === "All orders"
          ? {
              page: pageState?.page - 1,
              pageSize: pageState.pageSize,
              locationId: locId,
              orderType: retailType === "RESTAURANT" ? "RESTAURANT_ORDER" : "POS_ORDER",
              orderTypes: ["SWIGGY_ORDER", "ZOMATO_ORDER", "RESTAURANT_ORDER", "ONDC_ORDER"],
              ...(activeOrderType !== "all" && { subOrderTypes: [activeOrderType] }),
            }
          : orderValue?.value === "Cashier orders"
          ? {
              page: pageState.page - 1,
              pageSize: pageState.pageSize,
              locationId: locId,
              orderType: retailType === "RESTAURANT" ? "RESTAURANT_ORDER" : "POS_ORDER",
              orderTypes: ["SWIGGY_ORDER", "ZOMATO_ORDER", "RESTAURANT_ORDER", "ONDC_ORDER"],
              loggedInUserId: uidx,
              ...(activeOrderType !== "all" && { subOrderTypes: [activeOrderType] }),
            }
          : orderValue?.value === "App orders"
          ? {
              page: pageState?.page - 1,
              pageSize: pageState.pageSize,
              locationId: locId,
              orderType: "B2C_ORDER",
              orderTypes: ["SWIGGY_ORDER", "ZOMATO_ORDER", "RESTAURANT_ORDER", "ONDC_ORDER"],
              ...(activeOrderType !== "all" && { subOrderTypes: [activeOrderType] }),
            }
          : orderValue?.value === "Swiggy/Zomato"
          ? {
              page: pageState?.page - 1,
              pageSize: pageState.pageSize,
              locationId: locId,
              orderType: "B2C_ORDER",
              orderTypes: ["SWIGGY_ORDER", "ZOMATO_ORDER", "ONDC_ORDER"],
              ...(activeOrderType !== "all" && { subOrderTypes: [activeOrderType] }),
            }
          : orderValue?.value === "Credit Orders Listing"
          ? {
              page: pageState?.page - 1,
              pageSize: pageState.pageSize,
              orderType: "POS_ORDER",
              locationId: locId,
              paymentType: "POS_CREDIT_SALE",
              ...(activeOrderType !== "all" && { subOrderTypes: [activeOrderType] }),
            }
          : null;

      if (filterObject && date) {
        filterObject.startDate = format(date, "yyyy-MM-dd");
        filterObject.endDate = format(date, "yyyy-MM-dd");
      }
    } else {
      // The returns endpoint (/returns/v1/return-filter) searches via the
      // `returnId` field, not `searchBox` (which belongs to the order-filter
      // endpoint). This matches the original working returns search (commit
      // e05a7327) before the returns revamp dropped it.
      filterObject = {
        returnId: getReturnsText,
        page: pageState?.page - 1,
        pageSize: pageState.pageSize,
        orderType: retailType === "RESTAURANT" ? "RESTAURANT_ORDER" : "POS_ORDER",
        orderTypes: ["SWIGGY_ORDER", "ZOMATO_ORDER", "RESTAURANT_ORDER", "ONDC_ORDER"],
      };
    }

    if (!filterObject) return;
    let cancelled = false;

    setPageState((old) => ({ ...old, loader: true }));
    setLoader(true);
    returnsList(filterObject)
      .then((responseTxt) => {
        if (cancelled) return;
        showSnackbar("Successfully fetched the orders", "success");
        const dataArr = responseTxt?.data?.data?.orderResponseList;
        const mappedRows = dataArr?.map((row) => {
          return {
            date: row?.baseOrderResponse?.createdAt
              ? convertToLocalDateTime(row?.baseOrderResponse?.createdAt)
              : "-----",
            date_display: row?.baseOrderResponse?.createdAt
              ? format(new Date(row?.baseOrderResponse?.createdAt), "dd MMM yyyy")
              : "-----",
            time_display: row?.baseOrderResponse?.createdAt
              ? format(new Date(row?.baseOrderResponse?.createdAt), "hh:mm aa")
              : "",
            order_id: row?.baseOrderResponse?.orderId ?? "-----",
            return_id: row?.returnDetails?.returnId,
            customer_number_name:
              row?.baseOrderResponse?.customerName ||
              row?.baseOrderResponse?.mobileNumber ||
              "-----",
            customer_name: row?.baseOrderResponse?.customerName || "-----",
            customer_phone: row?.baseOrderResponse?.mobileNumber || "",
            cashier_name: row?.returnDetails?.createdBy,
            returned_count:
              //  row?.baseOrderResponse?.numberOfReturnedItems ||
              //   "N/A" / row?.returnDetails?.totalReturnedQuantity ||
              //   "N/A",
              row?.baseOrderResponse?.numberOfReturnedItems &&
              row?.baseOrderResponse?.totalReturnedQuantity
                ? `${row?.baseOrderResponse?.numberOfReturnedItems} items / ${row?.baseOrderResponse?.totalReturnedQuantity} qty`
                : "N/A",
            return_amount: row?.returnDetails?.refundAmount,
            status: row?.returnDetails?.returnStatus ?? "-----",
            payment_mode: row?.returnDetails?.returnMode,
          };
        });

        setTableRows(mappedRows);
        setSelectedRow([mappedRows?.[0]?.order_id]);
        setNoData(false);
        setLoader(false);
        setPageState((old) => ({
          ...old,
          loader: false,
          datRows: mappedRows || [],
          total: responseTxt?.data?.data?.totalResults || 0,
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        showSnackbar(err?.response?.data?.message || err?.message, "error");
        setLoader(false);
        setPageState((old) => ({ ...old, loader: false }));
      });
    return () => { cancelled = true; };
  }, [
    pageState?.page,
    pageState?.pageSize,
    update,
    getReturnsText,
    orderValue?.value,
    date,
    activeOrderType,
  ]);

  const orderDetailsPage = (id, retId, event) => {
    if (event?.type === "keydown" && (event?.key === "Tab" || event?.key === "Shift")) return;
    if (retId) setReturnId(retId);
    if (id) setOrderId(id);
    setState(true);
    setShowOrderDetails(true);
  };

  const handlePageChange = (event, value) => {
    setPageState((old) => ({ ...old, page: value }));
  };

  // 0-indexed handler for CustomerPaginatedTable
  const handleCptPageChange = (newPage) => {
    setPageState((old) => ({ ...old, page: newPage + 1 }));
  };

  useEffect(() => {
    const filteredRows = dataRows?.filter((row) => row?.order_id === selectedRow?.[0]);
    setRowDetails(filteredRows);
  }, [selectedRow]);

  const handleKeyDown = (event) => {
    if (state) return;
    if (event.key === "ArrowLeft" && event.shiftKey) {
      setPageState((old) => ({ ...old, page: Math.max(old.page - 1, 1) }));
    } else if (event.key === "ArrowRight" && event.shiftKey) {
      setPageState((old) => ({
        ...old,
        page: Math.min(old.page + 1, Math.ceil(old.total / old.pageSize)),
      }));
    } else if (event.key === "Enter" && rowDetails) {
      orderDetailsPage(rowDetails?.[0]?.order_id, rowDetails?.[0]?.return_id, {});
    }
  };

  useEffect(() => {
    const handleKeyDownGrid = (event) => {
      if (event.altKey && event.key === "a") {
        const firstRow = document.querySelector(".datagrid-rows");
        if (firstRow) firstRow.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDownGrid);
    return () => document.removeEventListener("keydown", handleKeyDownGrid);
  }, []);

  const handleNewOrderReturn = () => setOpen(true);
  const handleModalClose = () => setOpen(false);

  const handleReturnNavigate = () => {
    setLoader(true);
    getOrderDetails(orderIdVal, locId)
      .then((res) => {
        if (
          res?.data?.data?.baseOrderResponse?.orderStatus === "PAYMENT_PENDING" ||
          res?.data?.data?.baseOrderResponse?.orderStatus === "PARTIALLY_PAID"
        ) {
          showSnackbar("RETURN IS NOT ALLOWED FOR UNPAID OR PARTIALLY PAID ITEMS", "error");
          setLoader(false);
          return;
        }
        if (
          res?.data?.data?.baseOrderResponse?.refund ===
            res.data?.data?.baseOrderResponse?.amountPaid ||
          res?.data?.data?.baseOrderResponse?.orderType === "B2C_ORDER" ||
          res?.data?.data?.baseOrderResponse?.orderStatus === "CANCELLED"
        ) {
          showSnackbar(
            "RETURN IS NOT ALLOWED FOR ALREADY COMPLETED RETURN, CANCELLED OR APP ORDERS FROM POS",
            "error"
          );
          setOrderItems(res?.data?.data?.baseOrderResponse?.lineItems);
          setLoader(false);
          return;
        }
        const updatedLineItems = res?.data?.data?.baseOrderResponse?.lineItems
          ?.filter((item) => {
            const mainQty = item?.weightsAndMeasures ? item?.quantityBySpecs : item?.quantity;
            return item?.returnedQuantity === null || item?.returnedQuantity < mainQty;
          })
          ?.map((item) => {
            const mainQty = item?.weightsAndMeasures ? item?.quantityBySpecs : item?.quantity;
            const adjustedQty =
              item?.returnedQuantity !== null ? mainQty - item?.returnedQuantity : mainQty;
            return {
              ...item,
              ...(item?.weightsAndMeasures
                ? { quantityBySpecs: String(adjustedQty) }
                : { quantity: adjustedQty }),
            };
          });

        setOrderItems(updatedLineItems);
        setLoader(false);
        navigate(`/returns/${res?.data?.data?.baseOrderResponse?.orderId}`, {
          state: {
            orderItems: updatedLineItems,
            createdAt: res?.data?.data?.baseOrderResponse?.createdAt,
            paymentMethod: res?.data?.data?.orderBillingDetails?.paymentMethod,
          },
        });
      })
      .catch((err) => {
        setLoader(false);
        showSnackbar(err?.response?.data?.message || err?.message, "error");
      });
  };

  // const hanldeManagerAuth = () => openAuthModal(() => handleNewOrderReturn());
  const hanldeManagerAuth = () => handleNewOrderReturn();

  // Mobile render helper (unchanged)
  const renderOrders = () => {
    if (pageState.loader) {
      return (
        <Box className="order-loader-container">
          <CircularProgress />
        </Box>
      );
    }
    if (!dataRows?.length || orderValue === "In-store Delivery") {
      return (
        <Box className="order-loader-container">
          <Typography variant="h6">No Returns Found</Typography>
        </Box>
      );
    }
    return (
      <Box className="order-main-container">
        <Box className="order-item-container">
          {dataRows?.map((order, index) => (
            <OrderCard key={index} order={order} orderDetailsPage={orderDetailsPage} />
          ))}
        </Box>
        <Box className="order-pagination-container">
          <Pagination
            count={Math.ceil(pageState.total / pageState.pageSize)}
            page={pageState.page}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
            className="order-pagination"
          />
        </Box>
      </Box>
    );
  };

  const handleBack = () => setShowOrderDetails(false);

  // Row click for CustomerPaginatedTable
  const handleRowClick = (row) => {
    // orderDetailsPage(row?.order_id, row?.return_id, {});
    navigate(`/returns/details?returnid=${row?.return_id}&orderid=${row?.order_id}`);
  };

  return (
    <>
      {renderAuthModal()}

      {showOrderDetails ? (
        /* ── Order details view ─────────────────────────────────────────────── */
        <div className="order-details-page">
          <OrderDetailsPage
            orderId={orderId}
            setUpdate={setUpdate}
            update={update}
            setState={setState}
            setCustomerPage={() => {}}
            state={state}
            orderValue={orderValue}
            type={"returns"}
            returnId={returnId}
            handleBack={handleBack}
          />
        </div>
      ) : isMobile ? (
        /* ── Mobile list view ───────────────────────────────────────────────── */
        renderOrders()
      ) : (
        /* ── Desktop view ───────────────────────────────────────────────────── */
        <section className="returnScreenContainer">
          {!open ? (
            <div className="return-main-box">
              {noData ? (
                <SoftBox className="customer-no-data-box">
                  <SoftBox
                    sx={{
                      height: "525px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <h3>No Returns Found</h3>
                  </SoftBox>
                </SoftBox>
              ) : (
                <SoftBox>
                  {/* ── NEW: filter header ──────────────────────────────────── */}
                  <header className="returns-header">
                    <div
                      className={`returns-header__row ${
                        retailType === "RESTAURANT"
                          ? "returns-header___justify-space-between"
                          : "returns-header___justify-end"
                      } `}
                    >
                      {/* Pill tab group */}
                      {retailType === "RESTAURANT" && (
                        <div className="returns-header__tabs-scroll">
                          <nav className="returns-header__tabs" aria-label="Order type filter">
                            <ul
                              className="returns-header__tabs-inner"
                              role="list"
                              style={{ listStyle: "none", margin: 0, padding: 0 }}
                            >
                              {ORDER_TYPE_TABS?.map((tab) => (
                                <li key={tab?.value}>
                                  <button
                                    type="button"
                                    data-testid={tab?.testId}
                                    aria-pressed={activeOrderType === tab?.value}
                                    className={clsx(
                                      "returns-header__tab",
                                      activeOrderType === tab?.value &&
                                        "returns-header__tab--active"
                                    )}
                                    onClick={() => setActiveOrderType(tab?.value)}
                                  >
                                    {tab?.label}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </nav>
                        </div>
                      )}

                      {/* New Return CTA */}
                      <button
                        type="button"
                        data-testid="button-returns-new-return"
                        className="returns-header__new-btn"
                        onClick={hanldeManagerAuth}
                      >
                        <Undo2Icon />
                        New Return
                      </button>
                    </div>
                  </header>
                  {/* ── END filter header ───────────────────────────────────── */}

                  {/* ── OLD toolbar (commented out, preserved) ─────────────────
                  <Box className="return-check-box-wrapper" sx={{ justifyContent: "flex-end" }}>
                    <div className="order-select-wrapper">
                      <StyledSelect onChange={handleSelectChange} value={orderValue} className="order-select" options={orderSelectOptions} autoFocus={false} />
                      <StyledDatePicker placeholderText="Select Date" value={date} onChange={(e) => setDate(e)} dateFormat="d MMM, yyyy" icon={<DateRangeIcon fontSize="small" />} isClearable showIcon={!date} />
                    </div>
                    <div className="return-skipBtnContainer">
                      <Stack direction="column" spacing={2}>
                        <div className="btn-container-new">
                          <PosCustomButton title={"New Return"} variant={"blue-d"} onClickFunction={hanldeManagerAuth} p={"10px 12px 10px 12px"} />
                        </div>
                      </Stack>
                    </div>
                  </Box>
                  ─────────────────────────────────────────────────────────────── */}

                  {pageState.loader ? (
                    <SoftBox
                      sx={{
                        height: "525px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Spinner />
                    </SoftBox>
                  ) : (
                    <div ref={tableRef} className="returns-table-wrapper orders-responsive-table" onKeyDown={handleKeyDown} tabIndex={-1}>
                      {/* ── CustomerPaginatedTable (replaces MUI DataGrid) ── */}
                      <CustomerPaginatedTable
                        columns={RETURNS_COLUMNS}
                        rows={pageState?.datRows}
                        loading={pageState?.loader}
                        meta={{
                          page: pageState?.page - 1, // CPT is 0-indexed
                          pageSize: pageState?.pageSize,
                          totalResults: pageState?.total,
                        }}
                        onPageChange={handleCptPageChange}
                        emptyText="No Returns Found"
                        rowKey="order_id"
                        onRowClick={handleRowClick}
                        showPagination={true}
                      />

                      {/* ── Old MUI DataGrid (commented out, preserved) ──────
                    <SoftBox sx={{ height: "525px" }}>
                      <Box className="return-check-box-wrapper">
                        <div className="order-select-wrapper">
                          <StyledSelect
                            onChange={handleSelectChange}
                            value={orderValue}
                            className="order-select"
                            options={orderSelectOptions}
                            autoFocus={false}
                          />
                          <StyledDatePicker
                            placeholderText="Select Date"
                            value={date}
                            onChange={(e) => setDate(e)}
                            dateFormat="d MMM, yyyy"
                            icon={<DateRangeIcon fontSize="small" />}
                            isClearable
                            showIcon={!date}
                          />
                        </div>
                        <div className="return-skipBtnContainer">
                          <Stack direction="column" spacing={2}>
                            <div className="btn-container-new">
                              <PosCustomButton
                                title={"New Return"}
                                variant={"blue-d"}
                                onClickFunction={hanldeManagerAuth}
                                p={"10px 12px 10px 12px"}
                              />
                            </div>
                          </Stack>
                        </div>
                      </Box>
                      <div onKeyDown={handleKeyDown} tabIndex={-1}>
                        <DataGrid
                          rows={pageState.datRows}
                          columns={columns}
                          getRowId={(row) => row.order_id}
                          rowCount={parseInt(pageState.total)}
                          loading={pageState.loader}
                          pagination
                          page={pageState.page - 1}
                          pageSize={pageState.pageSize}
                          rowsPerPageOptions={[8]}
                          paginationMode="server"
                          onPageChange={(newPage) => {
                            setPageState((old) => ({ ...old, page: newPage + 1 }));
                          }}
                          getRowClassName={(params) =>
                            params.row.order_id === selectedRow[0] ||
                            params.row.order_id === selectedRow
                              ? ""
                              : ""
                          }
                          onCellClick={(row, event) => {
                            orderDetailsPage(row.id, row?.row?.return_id, event);
                          }}
                          onSelectionModelChange={(newSelection) => {
                            if (newSelection) {
                              setSelectedRow(newSelection);
                            } else {
                              setSelectedRow(null);
                            }
                          }}
                          hideScrollbar={true}
                          disableSelectionOnClick
                          // autoHeight={true}
                          sx={{
                            minHeight: "60vh",
                            border: "none",
                            "& .MuiDataGrid-columnHeaders": {
                              borderLeft: "none",
                              borderRight: "none",
                              borderTopRightRadius: "0px",
                              borderTopLeftRadius: "0px",
                              minHeight: "45px !important",
                              maxHeight: "48px !important",
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                              color: "black !important",
                              fontWeight: 700,
                            },
                            "& .MuiDataGrid-cellContent": {
                              fontWeight: 600,
                            },
                            "& .super-app.Paid": {
                              color: "#69E86D",
                              fontSize: "1em",
                              fontWeight: "600",
                              margin: "0px auto 0px auto",
                              padding: "5px",
                            },
                            "& .super-app.Create": {
                              color: "blue",
                              fontSize: "1em",
                              fontWeight: "600",
                              margin: "0px auto 0px auto",
                              padding: "5px",
                            },
                            "& .super-app.Adjust": {
                              color: "darkred",
                              fontSize: "1em",
                              fontWeight: "600",
                              margin: "0px auto 0px auto",
                              padding: "5px",
                            },
                            "& .super-app.Pending": {
                              color: "orange",
                              fontSize: "1em",
                              fontWeight: "600",
                              margin: "0px auto 0px auto",
                              padding: "5px",
                            },
                            "& .super-app.Cancel": {
                              color: "red",
                              fontSize: "1em",
                              fontWeight: "600",
                              margin: "0px auto 0px auto",
                              padding: "5px",
                            },
                            "& .super-app.Payment_Cancel": {
                              color: "red",
                              fontSize: ".75em",
                              fontWeight: "900",
                              margin: "0px auto 0px auto",
                              padding: "5px",
                            },
                            "& .super-app.Partial_Paid": {
                              color: "orange",
                              fontSize: "1em",
                              fontWeight: "900",
                              margin: "0px auto 0px auto",
                              padding: "5px",
                            },
                          }}
                        />
                        ──────────────────────────────────────────────────────── */}
                    </div>
                  )}
                </SoftBox>
              )}
            </div>
          ) : (
            <OrderIdValidationModal
              open={open}
              onClose={handleModalClose}
              setOrderIdVal={setOrderIdVal}
              orderIdVal={orderIdVal}
              loader={loader}
              handleReturnNavigate={handleReturnNavigate}
            />
          )}
        </section>
      )}
      {/* <Drawer
        sx={{ marginTop: "2.8rem", width: "100%", zIndex: "1300" }}
        open={state}
        onClose={() => setState(false)}
        anchor={"right"}
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : "550px",
          },
        }}
      >
        <div className="order-details-page">
          <OrderDetailsPage
            orderId={orderId}
            setUpdate={setUpdate}
            update={update}
            setState={setState}
            setCustomerPage={() => {}}
            state={state}
            orderValue={orderValue}
            type={"returns"}
            returnId={returnId}
          />
        </div>
      </Drawer> */}
    </>
  );
};
export default Returns;
